// src/components/OrderEntry/dialogs/DirectShipPatientDialog.jsx
// =============================================================================
// DIRECT SHIP TO PATIENT DIALOG
// =============================================================================

import React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { isCompleteDirectShipPatient, parseUsMailingAddress } from '../utils/directShipAddress.js';

const EMPTY_FORM = {
  studyId: '',
  studyAccessoryKit: '',
  patientDob: '',
  shipToName: '',
  address1: 'OR Residence',
  rawAddress: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  patientPhoneNumber: '',
};

const clean = (value) => String(value ?? '').trim();
const NAME_SUFFIX_RE = /^(JR|SR|II|III|IV|V|VI|VII|VIII|IX|X)\.?$/i;
const normalizeStudyKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

function parseDobToDate(value) {
  const raw = clean(value);
  if (!raw) {
    return null;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const d = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const mm = usMatch[1].padStart(2, '0');
    const dd = usMatch[2].padStart(2, '0');
    const d = new Date(`${usMatch[3]}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isDobUnder18(dobValue, now = new Date()) {
  const dobDate = parseDobToDate(dobValue);
  if (!dobDate || Number.isNaN(now.getTime())) {
    return false;
  }

  let age = now.getFullYear() - dobDate.getFullYear();
  const monthDiff = now.getMonth() - dobDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dobDate.getDate())) {
    age -= 1;
  }

  return age < 18;
}

export function toLastNameOnly(nameValue) {
  const original = clean(nameValue);
  if (!original) {
    return '';
  }

  if (original.includes(',')) {
    return clean(original.split(',')[0]);
  }

  const tokens = original.split(/\s+/).filter(Boolean);
  while (tokens.length > 1 && NAME_SUFFIX_RE.test(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  return clean(tokens[tokens.length - 1] || tokens[0] || '');
}

const getStudyLabel = (study) => {
  const studyId = clean(study?.studyId);
  return (
    clean(study?.label) ||
    `${studyId}-${clean(study?.patientName)}${
      clean(study?.patientDob) ? `-${clean(study?.patientDob)}` : ''
    }`
  );
};

export function filterStudiesBySearch(studies, searchText) {
  const options = Array.isArray(studies) ? studies : [];
  const needle = normalizeStudyKey(searchText);
  if (!needle) {
    return options;
  }

  return options.filter((option) => {
    const haystack = normalizeStudyKey(
      `${clean(option?.studyId)} ${clean(option?.patientName)} ${clean(option?.patientDob)}`
    );
    return haystack.includes(needle);
  });
}

export default function DirectShipPatientDialog({
  open,
  value,
  studies = [],
  studiesLoading = false,
  studiesError = '',
  onClose,
  onSubmit,
}) {
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [attempted, setAttempted] = React.useState(false);
  const [studySearch, setStudySearch] = React.useState('');
  const studyOptions = React.useMemo(() => (Array.isArray(studies) ? studies : []), [studies]);

  const filteredStudyOptions = React.useMemo(
    () => filterStudiesBySearch(studyOptions, studySearch),
    [studyOptions, studySearch]
  );

  const selectedStudy = React.useMemo(
    () => studyOptions.find((row) => clean(row?.studyId) === clean(form.studyId)) || null,
    [form.studyId, studyOptions]
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      ...EMPTY_FORM,
      ...(value && typeof value === 'object' ? value : {}),
      address1: 'OR Residence',
    });
    setStudySearch('');
    setAttempted(false);
  }, [open, value]);

  const update = (field) => (event) => {
    const nextValue = event.target.value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  // ---------------------------------------------------------------------------
  // BLOCK DSPD.PARSE.0 — Paste-friendly address parser
  // ---------------------------------------------------------------------------
  const handleRawAddressChange = (event) => {
    const rawAddress = event.target.value;
    const parsed = parseUsMailingAddress(rawAddress);

    setForm((prev) => ({
      ...prev,
      rawAddress,
      ...(parsed.address2 ? { address2: parsed.address2 } : {}),
      ...(parsed.city ? { city: parsed.city } : {}),
      ...(parsed.state ? { state: parsed.state } : {}),
      ...(parsed.zip ? { zip: parsed.zip } : {}),
    }));
  };

  const handleStudyChange = (selectedStudyValue) => {
    const selectedStudyId = clean(selectedStudyValue?.studyId);
    const study = selectedStudyValue || null;

    if (!study) {
      setForm((prev) => ({
        ...prev,
        studyId: selectedStudyId,
        studyAccessoryKit: '',
        patientDob: '',
      }));
      return;
    }

    const patientAddress = clean(study.patientAddress);
    const parsed = parseUsMailingAddress(patientAddress);

    setForm((prev) => ({
      ...prev,
      studyId: selectedStudyId,
      studyAccessoryKit: clean(study.studyAccessoryKit),
      patientDob: clean(study.patientDob),
      shipToName: clean(study.patientName) || prev.shipToName,
      rawAddress: patientAddress || prev.rawAddress,
      address2: parsed.address2 || prev.address2,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zip: parsed.zip || prev.zip,
      patientPhoneNumber: clean(study.patientPhoneNumber),
    }));
  };

  const normalized = {
    studyId: clean(form.studyId),
    studyAccessoryKit: clean(form.studyAccessoryKit),
    patientDob: clean(form.patientDob),
    shipToName: clean(form.shipToName),
    address1: 'OR Residence',
    rawAddress: clean(form.rawAddress),
    address2: clean(form.address2),
    city: clean(form.city),
    state: clean(form.state).toUpperCase().slice(0, 2),
    zip: clean(form.zip),
    patientPhoneNumber: clean(form.patientPhoneNumber),
  };

  const complete = isCompleteDirectShipPatient(normalized);

  // ---------------------------------------------------------------------------
  // BLOCK DSPD.SUBMIT.DEBOUNCE.0 — Debounced submit handler to prevent spamming
  // ---------------------------------------------------------------------------
  const lastSubmitTimeRef = React.useRef(0);
  const SUBMIT_DEBOUNCE_MS = 500;

  const handleSubmit = (event) => {
    // Prevent key-repeat when Enter is held down
    if (event?.repeat) {
      return;
    }

    const now = Date.now();
    if (now - lastSubmitTimeRef.current < SUBMIT_DEBOUNCE_MS) {
      return;
    }
    lastSubmitTimeRef.current = now;

    setAttempted(true);
    if (!complete) {
      return;
    }

    const isMinor = isDobUnder18(normalized.patientDob);
    const nextShipToName = isMinor ? toLastNameOnly(normalized.shipToName) : normalized.shipToName;

    onSubmit?.({
      ...normalized,
      shipToName: nextShipToName,
    });
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>You have selected Direct Ship to Patient</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="info">
            Enter the patient destination below. These values will replace the standard Ship To name
            and address when the order is submitted.
          </Alert>

          <Autocomplete
            options={filteredStudyOptions}
            loading={studiesLoading}
            autoComplete
            autoHighlight
            openOnFocus
            value={selectedStudy}
            inputValue={studySearch}
            getOptionLabel={(option) => getStudyLabel(option)}
            isOptionEqualToValue={(option, value) =>
              clean(option?.studyId) === clean(value?.studyId)
            }
            onChange={(event, newValue) => {
              handleStudyChange(newValue);
              setStudySearch(newValue ? getStudyLabel(newValue) : '');
            }}
            onInputChange={(event, newInputValue) => {
              setStudySearch(newInputValue);
            }}
            disabled={studiesLoading || studyOptions.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Study"
                placeholder="Type Study ID, Name, or DOB"
                helperText={
                  studiesLoading
                    ? 'Loading pending/recording studies...'
                    : studiesError ||
                      (studyOptions.length > 0
                        ? 'Type to refine by Study ID, Name, or DOB.'
                        : 'No pending or recording studies found for this facility ZIP.')
                }
              />
            )}
          />

          <TextField
            autoFocus
            required
            fullWidth
            label="Ship To Name"
            placeholder="Enter Patient Name"
            value={form.shipToName}
            onChange={update('shipToName')}
            error={attempted && !clean(form.shipToName)}
          />

          <TextField
            required
            fullWidth
            label="Address1"
            value="OR Residence"
            InputProps={{ readOnly: true }}
            helperText="Required routing label for patient shipments."
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Parse this address"
            placeholder="4490 SE 6th Way, Bushnell, FL 33513, USA"
            value={form.rawAddress}
            onChange={handleRawAddressChange}
            helperText="Paste a complete US address. Address2, City, State, and Zip will auto-populate."
          />

          <TextField
            required
            fullWidth
            label="Address2"
            placeholder="Enter Street Address"
            value={form.address2}
            onChange={update('address2')}
            error={attempted && !clean(form.address2)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              label="City"
              value={form.city}
              onChange={update('city')}
              error={attempted && !clean(form.city)}
            />
            <TextField
              required
              label="State"
              value={form.state}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  state: event.target.value
                    .toUpperCase()
                    .replace(/[^A-Z]/g, '')
                    .slice(0, 2),
                }))
              }
              inputProps={{ maxLength: 2 }}
              sx={{ width: { xs: '100%', sm: 120 } }}
              error={attempted && !/^[A-Za-z]{2}$/.test(clean(form.state))}
            />
            <TextField
              required
              label="Zip"
              placeholder="Zipcode"
              value={form.zip}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  zip: event.target.value.replace(/[^0-9-]/g, '').slice(0, 10),
                }))
              }
              sx={{ width: { xs: '100%', sm: 155 } }}
              error={attempted && !/^\d{5}(?:-\d{4})?$/.test(clean(form.zip))}
            />
          </Stack>

          {attempted && !complete && (
            <Alert severity="error">
              Complete all patient name and address fields before submitting.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Submit Patient Address
        </Button>
      </DialogActions>
    </Dialog>
  );
}
